import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn } from "lucide-react";
import axios from "axios";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { id: "all", label: "All" },
  { id: "culinary", label: "Culinary" },
  { id: "landscaping", label: "Landscaping" },
  { id: "community", label: "Community" },
];

export default function Gallery() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGalleryItems(response.data);
      } catch (error) {
        console.error("Error fetching gallery:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const filteredItems = activeCategory === "all"
    ? galleryItems
    : galleryItems.filter((item) => item.category === activeCategory);

  return (
    <div data-testid="gallery-page" className="pt-20">
      <SEO
        title="Photo Gallery"
        description="See Confidence 2 Create in action! Browse photos from our youth programs, community events, landscaping projects, and culinary training."
      />
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_c2c-nonprofit/artifacts/5cfuglox_Youth%201.jpg" 
            alt="C2C Team" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block bg-[#FFC107] text-[#0033A0] px-4 py-2 rounded-full text-sm font-bold mb-6">
              Our Work
            </span>
            <h1 className="text-3xl md:text-6xl font-black text-white mb-6">
              Photo <span className="text-[#FFC107]">Gallery</span>
            </h1>
            <p className="text-base md:text-xl text-white/90 max-w-2xl mx-auto">
              See our community in action! Browse photos from our programs, events, and the amazing work our youth are doing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-40 bg-white border-b border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                data-testid={`gallery-filter-${category.id}`}
                className={`px-5 py-2 rounded-full font-semibold whitespace-nowrap transition-all ${
                  activeCategory === category.id
                    ? "bg-[#0033A0] text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center py-20" data-testid="gallery-loading">
              <div className="w-12 h-12 border-4 border-[#0033A0] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <motion.div layout className="masonry-grid">
              <AnimatePresence>
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    className="group cursor-pointer"
                    onClick={() => setSelectedImage(item)}
                    data-testid={`gallery-item-${index}`}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-sm">
                      <div className={`aspect-${index % 3 === 0 ? 'square' : index % 3 === 1 ? 'video' : '[4/3]'}`}>
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                        />
                      </div>
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0033A0]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-white font-bold text-lg">{item.title}</h3>
                              <p className="text-white/80 text-sm">{item.description}</p>
                            </div>
                            <div className="w-10 h-10 bg-[#FFC107] rounded-full flex items-center justify-center">
                              <ZoomIn className="w-5 h-5 text-[#0033A0]" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="bg-[#FFC107] text-[#0033A0] px-3 py-1 rounded-full text-xs font-bold uppercase">
                          {item.category}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
              data-testid="lightbox-close-btn"
            >
              <X size={24} />
            </button>
            
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="max-w-5xl max-h-[90vh] relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <div className="mt-4 text-center">
                <h3 className="text-white text-2xl font-bold mb-2">{selectedImage.title}</h3>
                <p className="text-white/80">{selectedImage.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
